/**
 * ArchivedTemplateWarning
 *
 * Renders a prominent warning banner when the current LOR document was
 * generated from a template that has since been archived, superseded,
 * or deleted. Provides a CTA button to scroll back to the template
 * selector and regenerate the document with an active template.
 */
import { AlertTriangle, RefreshCw } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import type { TemplateStatus } from '@/services/api/generated/templatesV2Service';

interface ArchivedTemplateWarningProps {
  /** The status of the template (ARCHIVED, SUPERSEDED), or null if missing/deleted. */
  templateStatus: TemplateStatus | null;
  /** The name of the archived template, if available. */
  templateName?: string | null;
  /** Whether the template retrieve query errored (e.g. 404). */
  isError?: boolean;
  /** Callback to scroll to the template selector / trigger re-selection. */
  onSelectNewTemplate: () => void;
}

export const ArchivedTemplateWarning: React.FC<
  ArchivedTemplateWarningProps
> = ({ templateStatus, templateName, isError, onSelectNewTemplate }) => {
  const isMissing = isError || !templateStatus;
  const statusLabel = isMissing
    ? 'unavailable'
    : templateStatus === 'SUPERSEDED'
      ? 'superseded'
      : 'archived';

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-amber-800">
            Template {statusLabel}
          </h4>
          <p className="text-xs font-medium text-amber-700 mt-1">
            {isMissing ? (
              <>
                The template used to generate this LOR could not be found. It
                may have been deleted or is no longer accessible.
              </>
            ) : (
              <>
                The current LOR was generated using{' '}
                {templateName ? (
                  <span className="font-medium">
                    &quot;{templateName}&quot;
                  </span>
                ) : (
                  'a template'
                )}{' '}
                that has been {statusLabel}. Sending this document may contain
                outdated or incorrect content.
              </>
            )}
          </p>
          <p className="text-xs font-medium text-amber-700 mt-1">
            Please select an active template and regenerate the LOR before
            sending.
          </p>
          <div className="mt-3">
            <Button
              size="sm"
              variant="default"
              onClick={onSelectNewTemplate}
              className="gap-1.5"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Select Active Template &amp; Regenerate
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
